import { c as _c } from "react/compiler-runtime";
import { PricePlanType, PricePlanTypeEnum } from '@systemeio/types';
import { centsToUnits } from '@systemeio/util';
import { pricePlanTypeLocoKeys } from 'modules/digital-product/price-plans/constants/price-plan-type-loco-keys';
import { taxBehaviorLocoKeys } from 'modules/payment/tax-behavior';
import React, { ReactNode } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { twMerge } from 'tailwind-merge';

/**
 * `inline` keeps the label and the value in one flowing line, for the detail block of the attach
 * modal. `rows` gives each property its own two-column line, for the tooltip and hint popups.
 */
type PricePlanDetailsVariant = 'inline' | 'rows';
interface PricePlanDetailsProps {
  className?: string;
  pricePlan: PricePlanType;
  /** Rendered after the plan properties, for details the plan itself does not carry. */
  additionalRows?: ReactNode;
  /** Replaces the plan amount, for an order line whose price comes from the product. */
  amountOverride?: ReactNode;
  /**
   * Names the plan by its dashboard name and drops the order form name, the statement descriptor
   * and the type. For surfaces where the buyer-facing properties are noise.
   */
  hideOrderFormInfo?: boolean;
  variant?: PricePlanDetailsVariant;
}
interface PricePlanDetailRowProps {
  children: ReactNode;
  label: string;
  variant?: PricePlanDetailsVariant;
}

/**
 * One property line. Exported so a consumer can append rows of its own through `additionalRows`
 * without restating the layout.
 */
export const PricePlanDetailRow = t0 => {
  const $ = _c(4);
  const {
    children,
    label,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "rows" : t1;
  let t2;
  if ($[0] !== children || $[1] !== label || $[2] !== variant) {
    t2 = variant === "rows" ? <div className="flex items-center justify-between gap-3"><span className="text-text-tertiary shrink-0">{label}</span><span className="text-text-primary min-w-0 truncate text-right" title={typeof children === "string" ? children : undefined}>{children}</span></div> : <p className="text-text-secondary"><span className="text-text-primary">{label}: </span>{children}</p>;
    $[0] = children;
    $[1] = label;
    $[2] = variant;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};

/**
 * The properties of a price plan, shared by the detail block of the attach modal, the hint next
 * to a plan name and the plan options of the select.
 */
export function PricePlanDetails(t0) {
  const $ = _c(77);
  const {
    additionalRows,
    amountOverride,
    className,
    hideOrderFormInfo: t1,
    pricePlan,
    variant: t2
  } = t0;
  const hideOrderFormInfo = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "inline" : t2;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  let t3;
  if ($[0] !== pricePlan.amount || $[1] !== pricePlan.currency || $[2] !== user?.dashboardLocale) {
    t3 = currencyFormat(centsToUnits(pricePlan.amount), pricePlan.currency, user?.dashboardLocale);
    $[0] = pricePlan.amount;
    $[1] = pricePlan.currency;
    $[2] = user?.dashboardLocale;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const formattedAmount = t3;
  let t4;
  if ($[4] !== hideOrderFormInfo || $[5] !== t) {
    t4 = hideOrderFormInfo ? t("dashboard.funnel.configuration.common.price_plan.inner_name_label") : t("dashboard.funnel.configuration.common.price_plan.name_label");
    $[4] = hideOrderFormInfo;
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const nameLabel = t4;
  const nameValue = hideOrderFormInfo ? pricePlan.innerName : pricePlan.name;
  let t5;
  if ($[7] !== hideOrderFormInfo || $[8] !== pricePlan.statementDescriptor || $[9] !== t || $[10] !== variant) {
    t5 = !hideOrderFormInfo && pricePlan.statementDescriptor && <PricePlanDetailRow variant={variant} label={t("dashboard.funnel.configuration.common.price_plan.statement_descriptor")}>{pricePlan.statementDescriptor}</PricePlanDetailRow>;
    $[7] = hideOrderFormInfo;
    $[8] = pricePlan.statementDescriptor;
    $[9] = t;
    $[10] = variant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== hideOrderFormInfo || $[13] !== pricePlan.type || $[14] !== t || $[15] !== variant) {
    t6 = !hideOrderFormInfo && <PricePlanDetailRow variant={variant} label={t("dashboard.funnel.configuration.common.price_plan.type")}>{t(pricePlanTypeLocoKeys[pricePlan.type])}</PricePlanDetailRow>;
    $[12] = hideOrderFormInfo;
    $[13] = pricePlan.type;
    $[14] = t;
    $[15] = variant;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== pricePlan.recurringOptions || $[18] !== pricePlan.type || $[19] !== t || $[20] !== variant) {
    t7 = pricePlan.type === PricePlanTypeEnum.limited_subscription && <PricePlanDetailRow variant={variant} label={t("dashboard.funnel.configuration.common.price_plan.subscription_plan.limit_of_payments")}>{pricePlan.recurringOptions.limitOfPayments}</PricePlanDetailRow>;
    $[17] = pricePlan.recurringOptions;
    $[18] = pricePlan.type;
    $[19] = t;
    $[20] = variant;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== pricePlan.recurringOptions || $[23] !== t || $[24] !== variant) {
    t8 = pricePlan.recurringOptions && pricePlan.recurringOptions.trialPeriod > 0 && pricePlan.recurringOptions.trialInterval && <PricePlanDetailRow variant={variant} label={t("dashboard.funnel.configuration.common.price_plan.subscription_plan.trial_period")}>{t(`dashboard.funnel.configuration.common.price_plan.period.${pricePlan.recurringOptions.trialInterval}`, {
        count: pricePlan.recurringOptions.trialPeriod
      })}</PricePlanDetailRow>;
    $[22] = pricePlan.recurringOptions;
    $[23] = t;
    $[24] = variant;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t) {
    t9 = t("dashboard.funnel.configuration.common.price_plan.direct_charge_amount");
    $[26] = t;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== amountOverride || $[29] !== formattedAmount || $[30] !== pricePlan.amount || $[31] !== pricePlan.recurringOptions || $[32] !== pricePlan.type || $[33] !== t) {
    t10 = pricePlan.type === PricePlanTypeEnum.one_shot ? amountOverride ?? formattedAmount : pricePlan.amount && `${formattedAmount} ${t("dashboard.funnel.configuration.common.price_plan.subscription_plan.interval_label")} ${t(`dashboard.funnel.configuration.common.price_plan.period.${pricePlan.recurringOptions.interval}`, {
      count: pricePlan.recurringOptions.intervalCount
    })}`;
    $[28] = amountOverride;
    $[29] = formattedAmount;
    $[30] = pricePlan.amount;
    $[31] = pricePlan.recurringOptions;
    $[32] = pricePlan.type;
    $[33] = t;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t9 || $[37] !== variant) {
    t11 = <PricePlanDetailRow variant={variant} label={t9}>{t10}</PricePlanDetailRow>;
    $[35] = t10;
    $[36] = t9;
    $[37] = variant;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== pricePlan.taxBehavior || $[40] !== t || $[41] !== variant) {
    t12 = pricePlan.taxBehavior && <PricePlanDetailRow variant={variant} label={t("dashboard.payment.tax_behavior.title")}>{t(taxBehaviorLocoKeys[pricePlan.taxBehavior])}</PricePlanDetailRow>;
    $[39] = pricePlan.taxBehavior;
    $[40] = t;
    $[41] = variant;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== additionalRows || $[44] !== t11 || $[45] !== t12 || $[46] !== t5 || $[47] !== t6 || $[48] !== t7 || $[49] !== t8) {
    t13 = <>{t5}{t6}{t7}{t8}{t11}{t12}{additionalRows}</>;
    $[43] = additionalRows;
    $[44] = t11;
    $[45] = t12;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  const rows = t13;
  if (variant === "rows") {
    let t14;
    if ($[51] !== className) {
      t14 = twMerge("flex w-64 flex-col gap-3", className);
      $[51] = className;
      $[52] = t14;
    } else {
      t14 = $[52];
    }
    let t15;
    if ($[53] !== nameLabel) {
      t15 = <span className="text-text-primary">{nameLabel}</span>;
      $[53] = nameLabel;
      $[54] = t15;
    } else {
      t15 = $[54];
    }
    let t16;
    if ($[55] !== nameValue) {
      t16 = <span className="text-text-secondary">{nameValue}</span>;
      $[55] = nameValue;
      $[56] = t16;
    } else {
      t16 = $[56];
    }
    let t17;
    if ($[57] !== t15 || $[58] !== t16) {
      t17 = <div className="flex flex-col gap-1">{t15}{t16}</div>;
      $[57] = t15;
      $[58] = t16;
      $[59] = t17;
    } else {
      t17 = $[59];
    }
    let t18;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <hr className="border-border-200" />;
      $[60] = t18;
    } else {
      t18 = $[60];
    }
    let t19;
    if ($[61] !== rows) {
      t19 = <div className="flex flex-col gap-2">{rows}</div>;
      $[61] = rows;
      $[62] = t19;
    } else {
      t19 = $[62];
    }
    let t20;
    if ($[63] !== t14 || $[64] !== t17 || $[65] !== t19) {
      t20 = <div className={t14}>{t17}{t18}{t19}</div>;
      $[63] = t14;
      $[64] = t17;
      $[65] = t19;
      $[66] = t20;
    } else {
      t20 = $[66];
    }
    return t20;
  }
  let t14;
  if ($[67] !== className) {
    t14 = twMerge("flex min-w-0 flex-col items-start gap-1.5", className);
    $[67] = className;
    $[68] = t14;
  } else {
    t14 = $[68];
  }
  let t15;
  if ($[69] !== nameLabel || $[70] !== nameValue || $[71] !== variant) {
    t15 = <PricePlanDetailRow variant={variant} label={nameLabel}>{nameValue}</PricePlanDetailRow>;
    $[69] = nameLabel;
    $[70] = nameValue;
    $[71] = variant;
    $[72] = t15;
  } else {
    t15 = $[72];
  }
  let t16;
  if ($[73] !== rows || $[74] !== t14 || $[75] !== t15) {
    t16 = <div className={t14}>{t15}{rows}</div>;
    $[73] = rows;
    $[74] = t14;
    $[75] = t15;
    $[76] = t16;
  } else {
    t16 = $[76];
  }
  return t16;
}